import {
  Grid,
  Typography,
  TextField,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import TitleDefinition from "components/TitleDefinition";
import { CreateUpdateReductionMeasuresDesignProps } from "./data/CreateUpdateReductionMeasuresDesignProps";

import { TitleDefinitionConstants } from "constants/constant";
import {useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

import OpexComponent from "./OpexComponent";
import { useAppState } from "context/appState.context";

export default function MeasureFinancials(
  props: Readonly<CreateUpdateReductionMeasuresDesignProps>
) {
  const { draftReductionMeasure } = props;
  const { averageAnnualGrowthRate } = useAppState();
  const [openDialog, setOpenDialog] = useState(false);
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Grid>
      <Typography
        variant="h6"
        data-testid={"measureReduction"}
        sx={{ fontWeight: 600, fontSize: 12, mb: 2 }}
      >
        Measure Financials
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              data-testid={"capexinvestmentlabel"}
              sx={{ mr: 0.5, fontWeight: 600, fontSize: 12 }}
            >
              Total CapEx Cost
              <Typography component="span" sx={{ color: "red" }}>
                *
              </Typography>
            </Typography>
            <TitleDefinition
              content={
                TitleDefinitionConstants.ReductionMeasure.CapExInvestment
              }
              placement="auto"
            />
          </Stack>
          <TextField
            required
            id="capExInvestment"
            inputProps={{
              maxLength: 12,
              "data-testid": "capExInvestment",
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"capExInvestment"}
            value={props.draftReductionMeasure.capExInvestment ?? ""}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography variant="body1" data-testid={"opExLabel"} sx={{ fontWeight: 600, fontSize: 12 }}>
              Net Annual Maintenance OpEx
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.OpEx}
              placement="auto"
            />
            <Typography
              variant="body2"
              sx={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={handleOpenDialog}
            >
              OPEX projections
            </Typography>
          </Stack>
          <TextField
            id="opEx"
            inputProps={{
              maxLength: 12,
              "data-testid": "opEx",
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"opEx"}
            value={props.draftReductionMeasure.opEx ?? ""}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
       <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography variant="body1" data-testid={"externalFundingLabel"} sx={{ fontWeight: 600, fontSize: 12 }}>
              External Funding
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.ExternalFunding}
              placement="auto"
            />
          </Stack>
          <TextField
            id="externalFunding"
            inputProps={{
              maxLength: 6,
              "data-testid": "externalFunding",
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"externalFunding"}
            value={props.draftReductionMeasure.externalFunding ?? ""}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography variant="body1" data-testid={""} sx={{ fontWeight: 600, fontSize: 12 }}>
              Personnel Time
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.PersonnelTime}
              placement="auto"
            />
          </Stack>
          <TextField
            id="personnelTime"
            inputProps={{
              maxLength: 5,
              "data-testid": "personnelTime",
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"personnelTime"}
            value={props.draftReductionMeasure.personnelTime ?? ""}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography variant="body1" data-testid={"personnelRateLabel"} sx={{ fontWeight: 600, fontSize: 12 }}>
              Personnel Rate
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.PersonnelRate}
              placement="auto"
            />
          </Stack>
          <TextField
            id="personnelRate"
            inputProps={{
              maxLength: 6,
              "data-testid": "personnelRate",
              style: { fontSize: 12, padding: '4px 6px', height: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"personnelRate"}
            value={props.draftReductionMeasure.personnelRate ?? ""}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
        
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography variant="body1" data-testid={"financialNotesLabel"} sx={{ fontWeight: 600, fontSize: 12 }}>
            Financial Notes
          </Typography>
          <TextField
            type="text"
            id="financialNotes"
            multiline
            rows={2}
            inputProps={{
              flexWrap: "wrap",
              maxLength: 300,
              "data-testid": "financialNotes",
              style: { fontSize: 12, padding: '4px 6px', minHeight: 30 }
            }}
            fullWidth
            margin="normal"
            size="small"
            name={"financialNotes"}
            value={props.draftReductionMeasure.financialNotes}
            onChange={props.fieldChangeHandler}
            sx={{ fontSize: 12, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { minHeight: 30, fontSize: 12, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
      </Grid>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          OPEX projections
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <OpexComponent
            startYear={draftReductionMeasure.startYear ?? 2025}
            endYear={draftReductionMeasure.endYear ?? 2050}
            opexValue={draftReductionMeasure.opEx ?? 1}
            growthRate={averageAnnualGrowthRate}
            financialLifetime={Number(draftReductionMeasure.financialLifetime) || 0}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
